import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, CircularProgress, Avatar, Chip, alpha, useTheme } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import GradeSubjectGroups from "../List/GradeSubjectGroups";
import { useGetClassScheduleAvailableStaffMutation } from "../../../Api/attoDeskApi";

const fmtTime = (t?: string) => {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  const d = new Date(); d.setHours(h, m || 0);
  return new Intl.DateTimeFormat("en-CA", { hour: "numeric", minute: "numeric", hour12: true }).format(d);
};
const initials = (n: string) => (n || "").split(" ").filter(Boolean).slice(0, 2).map((x) => x[0]?.toUpperCase()).join("") || "?";
const toMin = (t: string) => { const [h, m] = String(t || "").split(":").map(Number); return (h || 0) * 60 + (m || 0); };

export interface AltPick { staffId: number; staffName: string; start: string; end: string }

/**
 * Availability-aware, academically-suitable staff selector for Create/Edit Class
 * Schedule. Only lists teachers who can teach the exact grade-subject AND are
 * genuinely free for the requested slot — an unsuitable or busy teacher can never
 * be selected. Busy-but-suitable staff and real alternative free windows are shown
 * informationally. Refetches whenever the class requirements or slot change,
 * ignores stale responses, and clears a selection that is no longer valid.
 * Powered by the shared /class-schedule/available-staff service.
 */
const AvailableStaffPicker: React.FC<{
  gradeId: string | number | null;
  subjectId: string | number | null;
  studentId?: string | number | null;
  date: string;                 // "YYYY-MM-DD"
  startTime: string;            // "HH:mm"
  endTime: string;              // "HH:mm"
  studyMode?: string;
  programType?: string;
  locationId?: string | number | null;
  excludeOccurrenceId?: number | null;
  value: string;                // selected staffId (string) or ""
  onChange: (staffId: string, staffName: string) => void;
  onPickAlternative?: (p: AltPick) => void;
}> = ({ gradeId, subjectId, studentId, date, startTime, endTime, studyMode, programType, locationId, excludeOccurrenceId, value, onChange, onPickAlternative }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const teal = "#0d9488";
  const [getAvailable, { isLoading }] = useGetClassScheduleAvailableStaffMutation();
  const [data, setData] = useState<any>(null);
  const [errored, setErrored] = useState(false);
  const seqRef = useRef(0);

  const ready = !!gradeId && !!subjectId && !!date && !!startTime && !!endTime && toMin(endTime) > toMin(startTime);

  const args = useMemo(() => ({
    grade_id: Number(gradeId) || undefined,
    subject_ids: subjectId ? [Number(subjectId)] : [],
    student_id: studentId ? Number(studentId) : undefined,
    date, start_time: startTime, end_time: endTime,
    study_mode: studyMode || "online",
    program_type: programType || undefined,
    location_id: locationId ? Number(locationId) : undefined,
    exclude_occurrence_id: excludeOccurrenceId || undefined,
  }), [gradeId, subjectId, studentId, date, startTime, endTime, studyMode, programType, locationId, excludeOccurrenceId]);

  useEffect(() => {
    if (!ready) { setData(null); setErrored(false); return; }
    const seq = ++seqRef.current;
    let alive = true;
    setErrored(false);
    (async () => {
      try {
        const res = await getAvailable(args).unwrap();
        if (!alive || seq !== seqRef.current) return; // ignore stale
        setData(res.status ? res.data : null);
        if (!res.status) setErrored(true);
      } catch { if (alive && seq === seqRef.current) { setData(null); setErrored(true); } }
    })();
    return () => { alive = false; };
  }, [args, ready, getAvailable]);

  const available: any[] = useMemo(() => (Array.isArray(data?.available_staff) ? data.available_staff : []), [data]);
  const unavailable: any[] = useMemo(() => (Array.isArray(data?.unavailable_staff) ? data.unavailable_staff : []), [data]);
  const alternatives: any[] = useMemo(() => (Array.isArray(data?.alternative_times) ? data.alternative_times : []), [data]);
  const studentConflict = data?.student_conflict;

  // Clear a selection that is no longer among the available staff (busy/unsuitable
  // after a requirement/time change). Guarded on a completed, non-loading load.
  useEffect(() => {
    if (!value) return;
    if (!ready) { onChange("", ""); return; }
    if (data && !available.some((s) => String(s.id) === String(value))) onChange("", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, ready]);

  if (!ready) {
    return <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>Select grade, subject, date and time first.</Typography>;
  }
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
        <CircularProgress size={16} /><Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>Checking matching staff availability…</Typography>
      </Box>
    );
  }
  if (errored) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, borderRadius: "8px", bgcolor: alpha("#ef4444", 0.06), border: `1px solid ${alpha("#ef4444", 0.25)}` }}>
        <ErrorOutlineIcon sx={{ fontSize: 16, color: "#b91c1c" }} />
        <Typography sx={{ fontSize: 12, color: "#b91c1c", fontWeight: 600 }}>Unable to check staff availability. Adjust the time and try again.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {studentConflict && (
        <Box sx={{ p: 1, borderRadius: "8px", bgcolor: alpha("#ef4444", 0.06), border: `1px solid ${alpha("#ef4444", 0.25)}` }}>
          <Typography sx={{ fontSize: 12, color: "#b91c1c", fontWeight: 600 }}>The student already has a class ({studentConflict.label}) at this time. Choose another time.</Typography>
        </Box>
      )}

      {available.length ? (
        available.map((s) => {
          const on = String(s.id) === String(value);
          const reasons: string[] = Array.isArray(s.match_reasons) ? s.match_reasons : [];
          return (
            <Box key={s.id} role="button" tabIndex={0}
              onClick={() => onChange(String(s.id), s.name)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onChange(String(s.id), s.name); } }}
              sx={{
                p: 1.25, borderRadius: "12px", cursor: "pointer", position: "relative",
                border: `1px solid ${on ? primary : s.is_best_match ? alpha(teal, 0.4) : "rgba(15,23,42,0.12)"}`,
                bgcolor: on ? alpha(primary, 0.06) : s.is_best_match ? alpha(teal, 0.05) : "#fff",
                "&:hover": { borderColor: on ? primary : teal },
              }}>
              {s.is_best_match && (
                <Chip icon={<AutoAwesomeIcon sx={{ fontSize: "13px !important" }} />} label="Best Match" size="small"
                  sx={{ position: "absolute", top: -10, left: 12, height: 20, fontSize: 10, fontWeight: 800, bgcolor: teal, color: "#fff", "& .MuiChip-icon": { color: "#fff" } }} />
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Avatar sx={{ width: 34, height: 34, fontSize: 13, fontWeight: 700, bgcolor: alpha(primary, 0.15), color: primary }}>{initials(s.name)}</Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{s.name}</Typography>
                  <Typography sx={{ fontSize: 11, color: teal, fontWeight: 600 }}>Available {fmtTime(startTime)}–{fmtTime(endTime)}</Typography>
                </Box>
                {on && <CheckCircleRoundedIcon sx={{ fontSize: 22, color: primary }} />}
              </Box>
              {!!(s.matched_grade_subjects?.length) && <Box sx={{ mt: 0.75 }}><GradeSubjectGroups groups={s.matched_grade_subjects} dense /></Box>}
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 0.25, mt: 0.5 }}>
                {reasons.map((r, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CheckCircleRoundedIcon sx={{ fontSize: 13, color: "#15803d" }} />
                    <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{r}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })
      ) : (
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontWeight: 600 }}>No suitable staff are available at this time.</Typography>
      )}

      {/* Suitable but busy — informational only, never selectable */}
      {!!unavailable.length && (
        <Box sx={{ mt: 0.5 }}>
          <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 700, mb: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
            <EventBusyOutlinedIcon sx={{ fontSize: 14 }} /> Suitable, but busy at this time
          </Typography>
          {unavailable.map((s) => (
            <Box key={s.id} sx={{ mb: 0.5, p: 0.75, borderRadius: "8px", border: "1px dashed rgba(15,23,42,0.18)" }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{s.name}<Typography component="span" sx={{ fontSize: 11, color: "text.secondary", fontWeight: 500 }}> — {s.reason}</Typography></Typography>
              {!!s.next_free?.length && (
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.3 }}>
                  {s.next_free.map((nf: any, i: number) => (
                    <Chip key={i} size="small" label={`${fmtTime(nf.start_time)}–${fmtTime(nf.end_time)}`}
                      onClick={() => onPickAlternative?.({ staffId: s.id, staffName: s.name, start: nf.start_time, end: nf.end_time })}
                      sx={{ height: 24, fontSize: 11, cursor: "pointer", bgcolor: alpha("#0f172a", 0.05) }} />
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Cross-staff alternative windows when nobody is free at the requested time */}
      {!available.length && !!alternatives.length && (
        <Box sx={{ mt: 0.5 }}>
          <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 700, mb: 0.5 }}>Suggested alternative times</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 0.5 }}>
            {alternatives.map((a, i) => (
              <Box key={i} role="button" tabIndex={0}
                onClick={() => onPickAlternative?.({ staffId: a.staff_id, staffName: a.staff_name, start: a.start_time, end: a.end_time })}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPickAlternative?.({ staffId: a.staff_id, staffName: a.staff_name, start: a.start_time, end: a.end_time }); } }}
                sx={{ p: 0.75, borderRadius: "9px", cursor: "pointer", border: "1px solid rgba(15,23,42,0.12)", display: "flex", alignItems: "center", gap: 1, "&:hover": { borderColor: primary } }}>
                <Avatar sx={{ width: 26, height: 26, fontSize: 10, bgcolor: alpha(primary, 0.12), color: primary }}>{initials(a.staff_name)}</Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{fmtTime(a.start_time)}–{fmtTime(a.end_time)}</Typography>
                  <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>{a.staff_name} · Use this time</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AvailableStaffPicker;
